/**
 * @file roles.js
 * @description Therapist role registry — single source of truth for role metadata.
 *   Defines credential formats, renewal cycles, and capability flags
 *   for RBT, BCBA, and BCaBA. Components should import from here instead of
 *   hardcoding role checks.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/roles
 */
// ─── Therapist Role Registry — single source of truth ────────────────────────
// Add future roles (BCBA-D, OT, SLP) here. Components should import from this
// file instead of hardcoding role checks. Keeps additions to one place.
//
// Role colors are NOT kept here and the label comes from typeLabel: src/theme.js (TYPE_COLOR, typeColor,
// roleColor, typeLabel) is the one source for both, so a role looks the same everywhere.
import { typeLabel } from "../theme";

export const THERAPIST_ROLES = {
  rbt: {
    id:                    "rbt",
    label:                 typeLabel("RBT"),
    longLabel:             "Registered Behavior Technician",
    credentialLabel:       "RBT Credential #",
    credentialPlaceholder: "RBT-XXXXXX",
    credentialRegex:       /^RBT-?\d{4,8}$/i,
    credentialHint:        "Format: RBT-XXXXXX. Find it on your BACB RBT certificate",
    caseType:              "RBT",
    warnDays:              30,
    renewalDesc:           "Annual Renewal",
    requiresSupervision:   true,        // RBTs must work under BCBA or BCaBA supervision at all times
    usesAdvancedScoring:       false,
    usesAssessmentsPanel:  false,
    skillsKey:             "RBT_SKILLS",
    signupDesc:            "Registered Behavior Technician",
  },
  bcba: {
    id:                    "bcba",
    label:                 typeLabel("BCBA"),
    longLabel:             "Board Certified Behavior Analyst",
    credentialLabel:       "BCBA Credential #",
    credentialPlaceholder: "1-XX-XXXXX",
    credentialRegex:       /^\d[\d-]{3,}$/i,
    credentialHint:        "Format: 1-XX-XXXXX. Your BACB certificant ID number",
    caseType:              "BCBA",
    warnDays:              60,
    renewalDesc:           "2-Year Cycle · 32 CEUs",
    requiresSupervision:   false,       // BCBAs practice independently
    usesAdvancedScoring:       true,
    usesAssessmentsPanel:  true,
    skillsKey:             "BCBA_SKILLS",
    signupDesc:            "Board Certified Behavior Analyst",
  },
  bcaba: {
    id:                    "bcaba",
    label:                 typeLabel("BCABA"),
    longLabel:             "Board Certified Assistant Behavior Analyst",
    credentialLabel:       "BCaBA Credential #",
    credentialPlaceholder: "1-XX-XXXXX",
    credentialRegex:       /^\d[\d-]{3,}$/i,
    credentialHint:        "Format: 1-XX-XXXXX. Your BACB BCaBA certificant ID number",
    caseType:              "BCABA",
    warnDays:              60,
    renewalDesc:           "2-Year Cycle · 32 CEUs",
    requiresSupervision:   true,        // BCaBAs must practice under BCBA supervision at all times (BACB Ethics 5.0)
    usesAdvancedScoring:       true,
    usesAssessmentsPanel:  true,
    skillsKey:             "BCABA_SKILLS",
    signupDesc:            "Board Certified Assistant Behavior Analyst",
  },
};

// ── Helper functions ──────────────────────────────────────────────────────────

/** Returns true if role is a therapist (rbt, bcba, or bcaba) */
export const isTherapistRole = (role) => role in THERAPIST_ROLES;

/** Returns the role config object, falling back to rbt if unknown */
export const getRoleConfig = (role) => THERAPIST_ROLES[role] || THERAPIST_ROLES.rbt;

/** Array of all therapist role IDs: ["rbt", "bcba", "bcaba"] */
export const therapistRoleIds = () => Object.keys(THERAPIST_ROLES);

/** True if role uses advanced scoring and profile sections (bcba, bcaba — not rbt) */
export const isAdvancedTherapistRole = (role) =>
  THERAPIST_ROLES[role]?.usesAdvancedScoring === true;

/** True if role can see the assessments panel (bcba, bcaba) */
export const usesAssessmentsPanel = (role) =>
  THERAPIST_ROLES[role]?.usesAssessmentsPanel === true;

/**
 * The therapist role a case TYPE belongs to, for grouping and benchmarks: "RBT" → "rbt", legacy "BT" → "rbt"
 * (a BT case is an RBT case), "BCaBA" → "bcaba". Mirrors case_type_benchmarks on the server.
 */
export const caseTypeRoleId = (type) => {
  const t = String(type || "").toLowerCase();
  return t === "bt" ? "rbt" : t;
};

/** Case type string for the given therapist role ("RBT", "BCBA", "BCABA") */
export const caseTypeForRole = (role) =>
  THERAPIST_ROLES[role]?.caseType ?? "RBT";

/** Expiry warning threshold in days for the given role */
export const warnDaysForRole = (role) =>
  THERAPIST_ROLES[role]?.warnDays ?? 30;

/** True if the role requires active BCBA supervision to practice (rbt, bcaba) */
export const requiresSupervision = (role) =>
  THERAPIST_ROLES[role]?.requiresSupervision === true;
